import { useId } from "react";

import type { ReadingReviewOut, ReviewWall, RingFix } from "@/api";
import { Button } from "@/components/Button";
import { formatMetres } from "@/lib/format";
import type { PageImage } from "@/lib/usePageImage";

import { fill, RING_TEXTS, ringFixParts } from "./model";
import styles from "./Review.module.css";
import { Sentence } from "./Sentence";
import { WallExcerpt } from "./WallExcerpt";

interface RingFixRowProps {
  fix: RingFix;
  wall: ReviewWall;
  image: PageImage;
  page: Pick<ReadingReviewOut, "pageWidthPx" | "pageHeightPx">;
  active: boolean;
  /** Take the suggested value — `how: gewaehlt`, `origin: ringfix`. */
  onTake: () => void;
  /** To the wall's own field, for a value from the sheet. */
  onTypeOwn: () => void;
  onActive: (how: "hover" | "focus", on: boolean) => void;
}

/**
 * „Ein Wert schließt den Umlauf“ (plan 4 §10.1) — the one suggestion the
 * machine makes about the ring, above the walls that need a person.
 *
 * Two independent pieces of evidence, and both are on screen: the person's
 * own handwriting around the wall (the excerpt), and the arithmetic in one
 * sentence — what the sheet shows, the value that closes the ring, and that
 * a comma moved. Nothing is set until a person presses the button.
 *
 * The button is outlined, not ink: it must never look like the way on. It is
 * no target of Enter — the field chain jumps from field to field and then to
 * „Übernehmen und planen“, never here — and nothing puts the focus on it.
 * Taken, the wall says „von dir“ like any figure a person set; the ring's
 * check then runs again, as after any figure.
 */
export function RingFixRow({ fix, wall, image, page, active, onTake, onTypeOwn, onActive }: RingFixRowProps) {
  const id = useId();
  const value = formatMetres(fix.valueMm);
  return (
    <div
      className={styles.fixRow}
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-sentence`}
      data-role="ring-fix"
      data-wall={wall.wallId}
      data-active={active}
      onPointerEnter={() => onActive("hover", true)}
      onPointerLeave={() => onActive("hover", false)}
      onFocus={() => onActive("focus", true)}
      onBlur={() => onActive("focus", false)}
    >
      <p id={`${id}-label`} className={`label ${styles.fixLabel}`}>
        Vorschlag zum Umlauf
      </p>
      <WallExcerpt wall={wall} image={image} page={page} />
      <p id={`${id}-sentence`} className={styles.fixSentence}>
        <Sentence parts={ringFixParts(fix, wall)} />
      </p>
      <div className={styles.fixActions}>
        {/* The sentence is the button's description too: tabbed onto it,
            a screen reader hears why before it takes anything. */}
        <Button variant="linie" data-role="ring-fix-take" aria-describedby={`${id}-sentence`} onClick={onTake}>
          <span className="num">{value}</span> übernehmen
        </Button>
        <Button variant="leise" onClick={onTypeOwn}>
          Anderen Wert eintippen
        </Button>
      </div>
    </div>
  );
}

/** What the screen reader hears once the suggestion is taken. */
export function ringFixTaken(fix: RingFix, wall: Pick<ReviewWall, "label">): string {
  return fill(RING_TEXTS.taken, { wall: wall.label, value: formatMetres(fix.valueMm) });
}
